/**
 * Product Recommendations Component
 * Handles product recommendations on the main product page
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.swiperInstance = null;
    this.handleLayoutChange = this.handleLayoutChange.bind(this);
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const target = this.dataset.target;
    const limit = this.dataset.limit || 4;
    const intent = this.dataset.intent || "related";

    // The desktop/mobile layout choice (carousel vs. grid) is decided at
    // this single breakpoint, matching the CSS `@media (width >= 64rem)`
    // cutoff used elsewhere in this section for the same purpose.
    this.mediaQuery = window.matchMedia("(min-width: 1024px)");
    this.mediaQuery.addEventListener("change", this.handleLayoutChange);

    // Check if recommendations already rendered server-side
    const existingContainer = this.querySelector(`[class*="${target}"]`);

    if (existingContainer && existingContainer.innerHTML.trim().length > 0) {
      console.log("Server-rendered recommendations found, initializing Swiper");
      this.updateLayoutMode(sectionId);
      return;
    }

    if (!productId) {
      console.error("Product ID is required for recommendations");
      return;
    }

    this.fetchRecommendations(productId, sectionId, target, limit, intent);
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleLayoutChange);
    }

    this.destroySwiper();
  }

  async fetchRecommendations(productId, sectionId, target, limit, intent) {
    this.abortController = new AbortController();

    try {
      const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;

      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");

      const recommendations = html.querySelector(`.${target}`);

      if (recommendations && recommendations.innerHTML.trim().length > 0) {
        this.innerHTML = recommendations.innerHTML;
        this.updateLayoutMode(sectionId);
      } else {
        console.log("No recommendations found for product:", productId);
        this.style.display = "none";

        this.dispatchEvent(
          new CustomEvent("recommendations:empty", {
            bubbles: true,
            detail: { productId },
          }),
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      console.error("Error fetching product recommendations:", error);
      this.style.display = "none";

      this.dispatchEvent(
        new CustomEvent("recommendations:error", {
          bubbles: true,
          detail: { error, productId },
        }),
      );
    }
  }

  // Re-evaluates which layout (carousel vs. grid) should be active for the
  // current viewport and initializes/destroys Swiper accordingly. Called
  // once after content is available, and again whenever the viewport
  // crosses the 1024px boundary so resizing the browser live still works.
  updateLayoutMode(sectionId) {
    this.sectionId = sectionId || this.sectionId;

    const isDesktop = this.mediaQuery ? this.mediaQuery.matches : window.innerWidth >= 1024;
    const layout = isDesktop ? this.dataset.layoutDesktop : this.dataset.layoutMobile;

    if (layout === "grid") {
      this.destroySwiper();
    } else {
      this.initializeSwiper(this.sectionId);
    }
  }

  handleLayoutChange() {
    this.updateLayoutMode(this.sectionId);
  }

  initializeSwiper(sectionId) {
    requestAnimationFrame(() => {
      const swiperEl = this.querySelector(`#swiper-${sectionId}`);
      const prevButton = this.querySelector(".carousel-button-prev");
      const nextButton = this.querySelector(".carousel-button-next");
      const pagination = this.querySelector(".carousel-progressbar");

      if (!swiperEl) {
        console.warn("Swiper element not found");
        return;
      }

      if (this.swiperInstance) {
        return;
      }

      // Below the 768px breakpoint, a mobile-carousel layout swipes through
      // a 2-column x 2-row grid per page (1 2 / 3 4) using Swiper's Grid
      // module — bundled in swiper-bundle.min.js, no extra import needed —
      // instead of a single scrolling row. This tier only ever runs when
      // layout_mobile is "carousel" (grid-layout mode never initializes
      // Swiper at all, see updateLayoutMode), so it's safe to key off it
      // directly. The 768/1024 breakpoints explicitly reset back to a single
      // row (grid: { rows: 1 }) since Swiper breakpoints only override keys
      // they specify — an unset `grid` at those tiers would otherwise keep
      // inheriting the 2-row grid from the base config below.
      const mobileGrid = this.dataset.layoutMobile === "carousel";

      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: mobileGrid ? 2 : 1.3,
        // 24px matches the gap used by the flat (non-carousel) 2-column
        // grid layout mode elsewhere in this section, for visual parity.
        spaceBetween: mobileGrid ? 24 : 16,
        grid: mobileGrid ? { rows: 2, fill: "row" } : { rows: 1 },
        navigation: {
          prevEl: prevButton,
          nextEl: nextButton,
        },
        pagination: {
          el: pagination,
          type: "progressbar",
        },
        breakpoints: {
          768: {
            slidesPerView: 2.3,
            grid: { rows: 1 },
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 32,
            grid: { rows: 1 },
          },
        },
      });
    });
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
